/** @format */

import axios from 'axios';
import toast from 'react-hot-toast';
import secureLocalStorage from 'react-secure-storage';
import { create } from 'zustand';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface User {
	fullName: string;
	emailId: string;
	phoneNumber: string;
	address: string;
	yearOfStudy: string;
	college: string;
	rollNumber: string;
	password: string;
	roleId: string;
	userId: string;
	token?: string;
	tcStatus?: string;
}

interface LoginCredentials {
	emailId: string;
	password: string;
	ipAddress?: string;
}

interface Subject {
	subjectName: string;
	subjectCode: string;
}

interface ApiResponse<T = any> {
	success: boolean;
	message?: string;
	user?: User;
	token?: string;
	data?: T;
}

interface AuthState {
	user: User | null;
	token: string | null;
	isLoading: boolean;
	error: string | null;
	forgotPasswordEmail: string | null;
	selectedSubject: Subject | null;
	setSelectedSubject: (subject: Subject | null) => void;
	login: (credentials: LoginCredentials) => Promise<boolean>;
	register: (userData: User) => Promise<boolean>;
	registerAdmin: (userData: User) => Promise<boolean>;
	fetchCurrentUser: () => Promise<void>;
	forgotPassword: (emailId: string) => Promise<boolean>;
	resendOtp: () => Promise<boolean>;
	updateUser: (updatedUser: Partial<User>) => Promise<void>;
	resetPassword: (
		emailId: string,
		otp: string,
		password: string
	) => Promise<boolean>;
	logout: () => Promise<void>;
	resetError: () => void;
	clearSession: (reason?: 'self' | 'expired') => void;
}

// Flag to prevent duplicate session expiration handling
let isHandlingSessionExpiration = false;

const api = axios.create({
	baseURL: API_BASE_URL,
	withCredentials: true,
	headers: { 'Content-Type': 'application/json' },
});

// Enhanced response interceptor to prevent duplicate session handling
api.interceptors.response.use(
	(response) => response,
	(error) => {
		if (axios.isAxiosError(error)) {
			const message = (error.response?.data as any)?.message;

			// Check for session expiration message
			if (
				(message === 'Session expired, please login again' ||
					error.response?.status === 401) &&
				!isHandlingSessionExpiration
			) {
				isHandlingSessionExpiration = true;
				const authStore = useAuthStore.getState();

				// Only show toast if user was actually logged in
				if (authStore.token || authStore.user) {
					authStore.clearSession('expired');
				}

				// Reset the flag after handling
				setTimeout(() => {
					isHandlingSessionExpiration = false;
				}, 1000);
			}
		}
		return Promise.reject(error);
	}
);

function getErrorMessage(error: unknown): string {
	if (axios.isAxiosError(error)) {
		return (
			(error.response?.data as any)?.message ||
			error.message ||
			'Server error occurred'
		);
	} else if (error instanceof Error) {
		return error.message;
	}
	return 'Unexpected error occurred';
}

export const useAuthStore = create<AuthState>((set, get) => ({
	user: null,
	token: null,
	isLoading: true,
	error: null,
	forgotPasswordEmail: null,
	selectedSubject: null,

	setSelectedSubject: (subject) => {
		if (subject) {
			sessionStorage.setItem('selectedSubject', JSON.stringify(subject));
		} else {
			sessionStorage.removeItem('selectedSubject');
		}

		set({ selectedSubject: subject });
	},

	login: async (credentials) => {
		set({ user: null, token: null, isLoading: false, error: null });
		try {
			const response = await api.post('/user/login', credentials);
			const user = response.data.success;
			if (!user || !user.token) {
				throw new Error('Login failed: Invalid email or password');
			}
			sessionStorage.setItem('user', JSON.stringify(user));
			sessionStorage.setItem('token', user.token);
			set({ user, token: user.token, isLoading: false });
			return user;
		} catch (err) {
			const msg = getErrorMessage(err);
			set({ error: msg, isLoading: false });
			toast.error(msg);
			return false;
		}
	},

	register: async (userData) => {
		set({ isLoading: true, error: null });
		try {
			const { data } = await api.post<ApiResponse<User>>('/user', userData);
			if (!data.success) {
				toast.error('Registration failed');
				set({ isLoading: false });
				return false;
			}
			toast.success('Registration successful!');
			set({ user: data.user, token: data.token, isLoading: false });
			return true;
		} catch (err) {
			const msg = getErrorMessage(err);
			toast.error(msg);
			set({ error: msg, isLoading: false });
			return false;
		}
	},

	registerAdmin: async (userData: User) => {
		set({ isLoading: true, error: null });
		try {
			const { data } = await api.post<ApiResponse<User>>('/user', userData);
			if (!data.success) {
				toast.error('Admin registration failed');
				set({ isLoading: false });
				return false;
			}
			set({ isLoading: false });
			return true;
		} catch (err) {
			const msg = getErrorMessage(err);
			toast.error(msg);
			set({ error: msg, isLoading: false });
			return false;
		}
	},

	forgotPassword: async (emailId) => {
		set({ isLoading: true, error: null });
		try {
			const { data } = await api.post<ApiResponse>('/user/forgotpassword', {
				emailId,
			});
			if (!data.success) throw new Error('Failed to send OTP');
			set({ isLoading: false, forgotPasswordEmail: emailId });
			return true;
		} catch (err) {
			const msg = getErrorMessage(err);
			set({ error: msg, isLoading: false });
			return false;
		}
	},

	resendOtp: async () => {
		const emailId = get().forgotPasswordEmail;
		if (!emailId) {
			const msg =
				'No email found to resend OTP. Please enter your email first.';
			toast.error(msg);
			set({ error: msg });
			return false;
		}

		set({ isLoading: true, error: null });
		try {
			const { data } = await api.post<ApiResponse>('/user/forgotpassword', {
				emailId,
			});
			if (!data.success) throw new Error('Failed to resend OTP');

			toast.success('OTP resent successfully!');
			set({ isLoading: false });
			return true;
		} catch (err) {
			const msg = getErrorMessage(err);
			toast.error(msg);
			set({ error: msg, isLoading: false });
			return false;
		}
	},

	resetPassword: async (emailId, otp, password) => {
		set({ isLoading: true, error: null });
		try {
			const { data } = await api.post<ApiResponse<User>>('/user/verifyotp', {
				emailId,
				otp,
				password,
			});

			if (!data.success) {
				toast.error('Invalid OTP');
				set({ isLoading: false });
				return false;
			}

			toast.success('Password changed successfully!');
			set({
				user: data.user || null,
				token: data.token || null,
				isLoading: false,
			});
			return true;
		} catch (err) {
			const msg = getErrorMessage(err);
			toast.error(msg);
			set({ error: msg, isLoading: false });
			return false;
		}
	},

	fetchCurrentUser: async () => {
		set({ isLoading: true });
		try {
			const userFromSession = sessionStorage.getItem('user');
			const tokenFromSession = sessionStorage.getItem('token');
			const subjectFromSession = sessionStorage.getItem('selectedSubject');

			let selectedSubject: Subject | null = null;
			if (subjectFromSession) {
				selectedSubject = JSON.parse(subjectFromSession);
			}

			if (userFromSession && tokenFromSession) {
				const user = JSON.parse(userFromSession);
				set({
					user,
					token: tokenFromSession,
					selectedSubject,
					isLoading: false,
				});
			} else {
				set({
					user: null,
					token: null,
					selectedSubject: null,
					isLoading: false,
				});
			}
		} catch {
			set({ user: null, token: null, isLoading: false });
		}
	},

	updateUser: async (updatedUser) => {
		set({ isLoading: true, error: null });

		try {
			const { user, token } = get();
			if (!user?.userId) throw new Error('User ID not found');
			if (!token) throw new Error('Token not available');

			const { data, status } = await api.put<ApiResponse<User>>(
				`/user?userId=${user.userId}`,
				updatedUser,
				{ headers: { Authorization: `Bearer ${token}` } }
			);

			if (status === 200 && data && data.success) {
				// toast.success("Profile updated successfully!");
				sessionStorage.setItem(
					'user',
					JSON.stringify({ ...user, ...updatedUser })
				);
				set({ user: { ...user, ...updatedUser }, isLoading: false });
			} else {
				// toast.error("Failed to update profile, please try again.");
				set({ error: 'Failed to update profile', isLoading: false });
			}
		} catch (err) {
			const msg = getErrorMessage(err);
			// toast.error(msg);
			set({ error: msg, isLoading: false });
		}
	},

	logout: async () => {
		try {
			const { user, token } = get();
			if (user?.userId && token) {
				await api.post(
					`/user/logout?userid=${user.userId}`,
					{},
					{
						headers: {
							Authorization: `Bearer ${token}`,
						},
						withCredentials: true,
					}
				);
			}
		} catch (err) {
			const msg = getErrorMessage(err);
			toast.error(msg);
		} finally {
			get().clearSession('self');
		}
	},

	clearSession: (reason?: 'self' | 'expired') => {
		const currentState = get();

		sessionStorage.clear();
		secureLocalStorage.removeItem('subjectCart');
		// Ensure selectedSubject is also cleared from state and storage (storage cleared by sessionStorage.clear())
		set({ user: null, token: null, selectedSubject: null });

		if (currentState.token || currentState.user) {
			if (reason === 'self') {
				toast.success('Logout successful!');
			} else {
				alert(
					'Multiple Active Sessions Detected\nYour account is already logged in on another device.\nPlease log out there before signing in again.'
				);
			}
		}
	},

	resetError: () => set({ error: null }),
}));
